import MuiAlert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AlertCircle, CheckCircle2, Info, TriangleAlert, X } from 'lucide-react';
import type { CSSProperties, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { dismissToast, TOAST_FADE_MS, type ToastVariant, useToasts } from '@/hooks/useToast';
import { cn } from '@/lib/cn';
import type { PendingKind } from '@/lib/pendingWrites';
import type { EmptyStateLook } from '@/theme/looks';
import { Button, IconButton } from './buttons';
import { Text } from './display';
import { ICON_SIZE } from './iconSize';

const ICON = {
  info: <Info size={ICON_SIZE.lg} />,
  success: <CheckCircle2 size={ICON_SIZE.lg} />,
  warning: <TriangleAlert size={ICON_SIZE.lg} />,
  error: <AlertCircle size={ICON_SIZE.lg} />,
};

export interface AlertProps {
  variant?: keyof typeof ICON;
  title?: string;
  /** An action, such as a retry button. */
  action?: ReactNode;
  onDismiss?: () => void;
  dismissLabel?: string;
  className?: string;
  children: ReactNode;
}

/** Shows a permanent MUI alert in the page, with an optional title and action. */
export function Alert({
  variant = 'info',
  title,
  action,
  onDismiss,
  dismissLabel,
  className,
  children,
}: AlertProps) {
  const { t } = useTranslation();
  const actions = (action || onDismiss) && (
    <span className="flex items-center gap-2">
      {action}
      {onDismiss && (
        <IconButton aria-label={dismissLabel ?? t('common.dismiss')} size="sm" onClick={onDismiss}>
          <X size={ICON_SIZE.md} />
        </IconButton>
      )}
    </span>
  );

  return (
    <MuiAlert
      severity={variant}
      role={variant === 'error' ? 'alert' : 'status'}
      icon={ICON[variant]}
      action={actions}
      className={className}
    >
      {title && <AlertTitle>{title}</AlertTitle>}
      {children}
    </MuiAlert>
  );
}

export interface LoadingProps {
  /** The text beside the spinner. Without it the text is "Loading". */
  label?: string;
  /** `section` fills a block and centres the spinner; `inline` sits in a line of text. */
  size?: 'section' | 'inline';
  className?: string;
}

/** Shows an MUI spinner and its label while something loads. It shows no data. */
export function Loading({ label, size = 'section', className }: LoadingProps) {
  const { t } = useTranslation();
  const section = size === 'section';

  return (
    <div
      role="status"
      className={cn('flex items-center justify-center gap-2', section && 'min-h-40 flex-col p-6', className)}
    >
      <CircularProgress size={section ? ICON_SIZE.xxl : ICON_SIZE.sm} aria-hidden />
      <Typography component="span" variant={section ? 'body1' : 'caption'} color="textSecondary">
        {label ?? t('common.loading')}
      </Typography>
    </div>
  );
}

export interface AsyncSectionProps {
  loading: boolean;
  error: Error | null;
  isEmpty?: boolean;
  empty?: ReactNode;
  onRetry?: () => void;
  children: ReactNode;
}

/** Shows a loader, an error with a retry, the empty state, or the content. */
export function AsyncSection({
  loading,
  error,
  isEmpty = false,
  empty,
  onRetry,
  children,
}: AsyncSectionProps) {
  const { t } = useTranslation();

  if (loading) return <Loading />;

  if (error) {
    return (
      <Alert
        variant="error"
        title={t('common.loadFailed')}
        action={onRetry ? <Button size="sm" variant="secondary" onClick={onRetry}>{t('common.retry')}</Button> : undefined}
      >
        {t('common.loadFailedDescription')}
      </Alert>
    );
  }

  if (isEmpty && empty) return <>{empty}</>;

  return <>{children}</>;
}

const Root = styled(Paper, { name: 'KitEmptyState', slot: 'root' })<{ ownerState: EmptyStateLook }>({});

export interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  /** The next action, such as a create button. */
  action?: ReactNode;
  /** Removes the border and the fill, for a whole page. */
  plain?: boolean;
  className?: string;
}

/** Shows an icon, a title, a description and an action for an empty list. */
export function EmptyState({ icon, title, description, action, plain = false, className }: EmptyStateProps) {
  return (
    <Root
      variant="outlined"
      ownerState={{ plain }}
      className={cn('flex min-h-empty-state flex-col items-center justify-center gap-2 p-6 text-center', className)}
    >
      {icon && <Box sx={{ color: 'text.secondary' }}>{icon}</Box>}
      <Typography variant="subtitle2" component="p">
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="textSecondary" className="max-w-sm">
          {description}
        </Typography>
      )}
      {action && <div className="mt-2">{action}</div>}
    </Root>
  );
}

const SIZE = { sm: ICON_SIZE.sm, md: ICON_SIZE.lg } as const;

export interface SpinnerProps {
  size?: keyof typeof SIZE;
  className?: string;
  /** Text for a screen reader. Omit this if the text near the spinner gives it. */
  label?: string;
}

/** Shows an MUI spinner in the colour of the text around it. */
export function Spinner({ size = 'md', className, label }: SpinnerProps) {
  return (
    <CircularProgress
      size={SIZE[size]}
      color="inherit"
      className={className}
      role={label ? 'status' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    />
  );
}

export interface PendingProps {
  /** The write this row waits on. Without one the row shows as it is. */
  write?: PendingKind;
  className?: string;
  children: ReactNode;
}

/** Shows a loader and the name of the write over one row, until the stored data shows that write.
 *  The row keeps its size but is hidden, so no data shows. */
export function Pending({ write, className, children }: PendingProps) {
  const { t } = useTranslation();

  if (!write) return <>{children}</>;

  return (
    <div aria-busy="true" className={cn('relative', className)}>
      <div aria-hidden style={{ visibility: 'hidden' }}>
        {children}
      </div>
      <Paper variant="outlined" className="absolute inset-0 flex items-center justify-center">
        <Loading size="inline" label={t(`common.pending.${write}`)} />
      </Paper>
    </div>
  );
}

export interface ProgressBarProps {
  /** Omit this for a bar that moves without an end, for work of unknown length. */
  value?: number;
  max?: number;
  label: string;
  showValue?: boolean;
  className?: string;
}

/** Shows an MUI linear progress bar, with an optional label and percentage above it. */
export function ProgressBar({ value, max = 100, label, showValue = false, className }: ProgressBarProps) {
  const indeterminate = value === undefined;
  const percent = indeterminate ? 0 : Math.min(100, Math.max(0, (value / max) * 100));

  return (
    <div className={cn('space-y-1', className)}>
      {showValue && (
        <Typography component="div" variant="caption" color="textSecondary" className="flex justify-between">
          <span>{label}</span>
          {!indeterminate && <Text mono>{Math.round(percent)}%</Text>}
        </Typography>
      )}
      <LinearProgress
        variant={indeterminate ? 'indeterminate' : 'determinate'}
        value={percent}
        aria-label={label}
        aria-valuenow={indeterminate ? undefined : value}
        aria-valuemax={indeterminate ? undefined : max}
      />
    </div>
  );
}

const TOAST_ICON: Record<ToastVariant, typeof Info> = {
  success: CheckCircle2,
  error: AlertCircle,
  warning: TriangleAlert,
  info: Info,
};

/** Shows the current set of toasts as MUI alerts. Mount this one time, in the app shell. */
export function ToastHost({ dismissLabel }: { dismissLabel?: string }) {
  const { t } = useTranslation();
  const toasts = useToasts();
  if (toasts.length === 0) return null;

  return createPortal(
    <div
      aria-live="polite"
      aria-relevant="additions"
      className="pointer-events-none fixed inset-x-4 bottom-4 z-50 flex flex-col gap-2 sm:inset-x-auto sm:right-4 sm:w-80"
    >
      {toasts.map((toast) => {
        const Icon = TOAST_ICON[toast.variant];
        const life = toast.duration ?? 0;
        const timed = life > 0;
        return (
          <MuiAlert
            key={toast.id}
            variant="outlined"
            severity={toast.variant}
            role={toast.variant === 'error' ? 'alert' : 'status'}
            icon={<Icon size={ICON_SIZE.md} aria-hidden />}
            action={
              <IconButton aria-label={dismissLabel ?? t('common.dismiss')} size="sm" onClick={() => dismissToast(toast.id)}>
                <X size={ICON_SIZE.sm} />
              </IconButton>
            }
            style={
              timed
                ? ({
                    '--toast-life': `${life}ms`,
                    '--toast-fade': `${Math.max(0, life - TOAST_FADE_MS)}ms`,
                  } as CSSProperties)
                : undefined
            }
            className={cn('pointer-events-auto relative overflow-hidden', timed ? 'toast-life' : 'fade-in')}
          >
            {toast.message}
            {timed && (
              <span
                aria-hidden
                className="toast-drain absolute inset-x-0 bottom-0 h-0.5 bg-current opacity-30"
              />
            )}
          </MuiAlert>
        );
      })}
    </div>,
    document.body,
  );
}
